import { useState, useCallback, useMemo, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { Seller } from '../types/invoiceTypes';
import { 
  OnboardingStepId, 
  OnboardingProgress, 
  ONBOARDING_STEPS,
  isLikelyCorporation 
} from '../types/onboardingTypes';
import logger from '../utils/logger';

interface UseOnboardingProgressReturn {
  // Aktueller Status
  currentStep: OnboardingStepId;
  progress: OnboardingProgress | null;
  isLoading: boolean;
  error: string | null;
  
  // Seller-Daten für das Interview
  interviewData: Partial<Seller>;
  
  // Prüfungen
  needsOnboarding: boolean;
  isStepCompleted: (stepId: OnboardingStepId) => boolean;
  shouldShowStep: (stepId: OnboardingStepId) => boolean;
  isCorporation: boolean;
  
  // Navigation
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  skipStep: () => void;
  
  // Speicherung
  saveStepData: (data: Partial<Seller>) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  
  // Fortschritt
  completionPercentage: number;
  totalSteps: number;
  currentStepIndex: number;
}

const INITIAL_PROGRESS: OnboardingProgress = {
  currentStep: 'welcome',
  completedSteps: [],
  startedAt: new Date().toISOString(),
  lastUpdatedAt: new Date().toISOString(),
  isCompleted: false,
  skippedSteps: [],
};

export const useOnboardingProgress = (): UseOnboardingProgressReturn => {
  const { currentUser, sellerData, saveSellerData, refreshUserData } = useAuth();
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interviewData, setInterviewData] = useState<Partial<Seller>>({});

  // Prüfe ob der Firmenname auf eine Kapitalgesellschaft hindeutet
  const isCorporation = useMemo(() => {
    const name = interviewData.name || sellerData?.name || '';
    return isLikelyCorporation(name);
  }, [interviewData.name, sellerData?.name]);

  // Filtere die Schritte basierend auf Bedingungen
  const applicableSteps = useMemo(() => {
    return ONBOARDING_STEPS.filter(step => {
      // Handelsregister nur für Kapitalgesellschaften anzeigen
      if (step.id === 'commercialRegister') {
        return isCorporation;
      }
      return true;
    });
  }, [isCorporation]);

  // Aktueller Schritt-Index
  const currentStepIndex = useMemo(() => {
    if (!progress) return 0;
    return applicableSteps.findIndex(s => s.id === progress.currentStep);
  }, [progress, applicableSteps]);

  // Prüfe ob ein Schritt basierend auf vorhandenen Daten übersprungen werden kann
  const isStepDataComplete = useCallback((stepId: OnboardingStepId): boolean => {
    const data = { ...sellerData, ...interviewData };
    
    switch (stepId) {
      case 'welcome':
        return false; // Welcome wird immer gezeigt beim ersten Mal
      case 'companyName':
        return !!(data.name && data.name.trim() !== '');
      case 'address':
        return !!(
          data.street && data.street.trim() !== '' &&
          data.zip && data.zip.trim() !== '' &&
          data.city && data.city.trim() !== '' &&
          data.country && data.country.trim() !== ''
        );
      case 'commercialRegister':
        // Nicht mehr als Pflicht behandeln - auch bei Kapitalgesellschaften optional
        // Der User wird nur darauf hingewiesen, dass es gesetzlich vorgeschrieben ist
        // Wir blockieren aber weder das Onboarding noch die Rechnungserstellung
        if (progress?.skippedSteps.includes('commercialRegister')) return true;
        return !!(data.commercialRegisterNumber && data.commercialRegisterLocation);
      case 'bankDetails':
        // Freiwillig - nur als complete markieren wenn bereits übersprungen wurde
        // oder wenn IBAN vorhanden ist
        if (progress?.skippedSteps.includes('bankDetails')) return true;
        return !!(data.bankDetails?.iban);
      case 'taxNumber':
        // Freiwillig - nur als complete markieren wenn bereits übersprungen wurde
        // oder wenn tatsächlich Daten vorhanden sind
        if (progress?.skippedSteps.includes('taxNumber')) return true;
        return !!(data.taxNumber || data.vatNumber);
      case 'logo':
        // Freiwillig - nur als complete markieren wenn bereits übersprungen wurde
        // oder wenn Logo vorhanden ist
        if (progress?.skippedSteps.includes('logo')) return true;
        return !!data.logoUrl;
      case 'completion':
        return false; // Completion wird immer gezeigt
      default:
        return false;
    }
  }, [sellerData, interviewData, progress?.skippedSteps]);

  // Prüfe ob Onboarding notwendig ist
  const needsOnboarding = useMemo(() => {
    if (!currentUser) return false;
    
    // Wenn Onboarding explizit abgeschlossen wurde
    if (progress?.isCompleted) return false;
    if (sellerData?.onboardingCompleted) return false;
    
    // Pflichtfelder prüfen
    const hasRequiredFields = !!(
      sellerData?.name &&
      sellerData?.street &&
      sellerData?.zip &&
      sellerData?.city &&
      sellerData?.country
    );
    
    // Wenn Pflichtfelder fehlen, brauchen wir Onboarding
    return !hasRequiredFields;
  }, [currentUser, progress?.isCompleted, sellerData]);

  // Lade den Fortschritt beim Initialisieren
  useEffect(() => {
    const loadProgress = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        const progressRef = doc(db, 'sellers', currentUser.uid);
        const docSnap = await getDoc(progressRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.onboardingProgress) {
            setProgress(data.onboardingProgress);
          } else {
            // Initialisiere neuen Fortschritt
            setProgress(INITIAL_PROGRESS);
          }
          
          // Setze Interview-Daten aus bestehenden Seller-Daten
          setInterviewData({
            name: data.name || '',
            street: data.street || '',
            zip: data.zip || '',
            city: data.city || '',
            country: data.country || 'DE',
            commercialRegisterNumber: data.commercialRegisterNumber || null,
            commercialRegisterLocation: data.commercialRegisterLocation || null,
            taxNumber: data.taxNumber || null,
            vatNumber: data.vatNumber || null,
            logoUrl: data.logoUrl || null,
            isSmallBusiness: data.isSmallBusiness || false,
          });
        } else {
          // Neuer Benutzer
          setProgress(INITIAL_PROGRESS);
          setInterviewData({
            country: 'DE',
          });
        }
      } catch (err) {
        logger.error(err, 'Error loading onboarding progress');
        setError('Fehler beim Laden des Onboarding-Fortschritts');
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [currentUser]);

  // Finde den nächsten nicht-ausgefüllten Schritt (intern verwendet)
  const findNextIncompleteStep = useCallback((): OnboardingStepId => {
    for (const step of applicableSteps) {
      if (step.id === 'welcome' && progress?.completedSteps.includes('welcome')) {
        continue;
      }
      if (step.id === 'completion') {
        continue;
      }
      if (!isStepDataComplete(step.id) && !progress?.skippedSteps.includes(step.id)) {
        return step.id;
      }
    }
    return 'completion';
  }, [applicableSteps, progress, isStepDataComplete]);

  // Exportiere findNextIncompleteStep für potenzielle Verwendung
  void findNextIncompleteStep;

  // Speichere Schritt-Daten sofort in Firebase
  const saveStepData = useCallback(async (data: Partial<Seller>) => {
    if (!currentUser) return;
    
    setError(null);
    
    try {
      // Update lokale Interview-Daten
      setInterviewData(prev => ({ ...prev, ...data }));
      
      // Speichere in Firebase
      await saveSellerData(data);
      
      logger.info('Onboarding step data saved', { fields: Object.keys(data) });
    } catch (err) {
      logger.error(err, 'Error saving onboarding step data');
      setError('Fehler beim Speichern der Daten');
      throw err;
    }
  }, [currentUser, saveSellerData]);

  // Aktualisiere den Fortschritt
  const updateProgress = useCallback(async (newProgress: Partial<OnboardingProgress>) => {
    if (!currentUser) return;

    const updatedProgress: OnboardingProgress = {
      ...progress!,
      ...newProgress,
      lastUpdatedAt: new Date().toISOString(),
    };

    try {
      const sellerRef = doc(db, 'sellers', currentUser.uid);
      await setDoc(sellerRef, {
        onboardingProgress: updatedProgress,
      }, { merge: true });
      
      setProgress(updatedProgress);
    } catch (err) {
      logger.error(err, 'Error updating onboarding progress');
      setError('Fehler beim Aktualisieren des Fortschritts');
    }
  }, [currentUser, progress]);

  // Gehe zum nächsten Schritt
  const goToNextStep = useCallback(async () => {
    if (!progress) return;

    const currentIdx = applicableSteps.findIndex(s => s.id === progress.currentStep);
    const completedSteps = [...progress.completedSteps];
    
    if (!completedSteps.includes(progress.currentStep)) {
      completedSteps.push(progress.currentStep);
    }

    // Finde den nächsten Schritt
    let nextStep: OnboardingStepId;
    if (currentIdx < applicableSteps.length - 1) {
      // Prüfe ob der nächste Schritt übersprungen werden kann
      let nextIdx = currentIdx + 1;
      while (nextIdx < applicableSteps.length - 1) {
        const step = applicableSteps[nextIdx];
        if (!isStepDataComplete(step.id) || step.id === 'completion') {
          break;
        }
        completedSteps.push(step.id);
        nextIdx++;
      }
      nextStep = applicableSteps[nextIdx].id;
    } else {
      nextStep = 'completion';
    }

    await updateProgress({
      currentStep: nextStep,
      completedSteps,
    });
  }, [progress, applicableSteps, updateProgress, isStepDataComplete]);

  // Gehe zum vorherigen Schritt
  const goToPreviousStep = useCallback(async () => {
    if (!progress) return;

    const currentIdx = applicableSteps.findIndex(s => s.id === progress.currentStep);
    if (currentIdx > 0) {
      await updateProgress({
        currentStep: applicableSteps[currentIdx - 1].id,
      });
    }
  }, [progress, applicableSteps, updateProgress]);

  // Überspringe einen optionalen Schritt
  const skipStep = useCallback(async () => {
    if (!progress) return;

    const skippedSteps = [...progress.skippedSteps];
    if (!skippedSteps.includes(progress.currentStep)) {
      skippedSteps.push(progress.currentStep);
    }

    await updateProgress({
      skippedSteps,
    });
    
    await goToNextStep();
  }, [progress, updateProgress, goToNextStep]);

  // Schließe das Onboarding ab
  const completeOnboarding = useCallback(async () => {
    if (!currentUser) return;

    try {
      // Markiere Onboarding als abgeschlossen
      await saveSellerData({
        onboardingCompleted: true,
        onboardingCompletedAt: new Date().toISOString(),
      } as Partial<Seller>);

      await updateProgress({
        isCompleted: true,
        currentStep: 'completion',
        completedSteps: applicableSteps.map(s => s.id),
      });

      // Refresh user data
      await refreshUserData();
      
      logger.info('Onboarding completed');
    } catch (err) {
      logger.error(err, 'Error completing onboarding');
      setError('Fehler beim Abschließen des Onboardings');
      throw err;
    }
  }, [currentUser, saveSellerData, updateProgress, applicableSteps, refreshUserData]);

  // Prüfe ob ein Schritt abgeschlossen ist
  const isStepCompleted = useCallback((stepId: OnboardingStepId): boolean => {
    return progress?.completedSteps.includes(stepId) || false;
  }, [progress]);

  // Prüfe ob ein Schritt angezeigt werden soll
  const shouldShowStep = useCallback((stepId: OnboardingStepId): boolean => {
    const step = ONBOARDING_STEPS.find(s => s.id === stepId);
    if (!step) return false;

    if (step.isConditional && step.id === 'commercialRegister') {
      return isCorporation;
    }

    return true;
  }, [isCorporation]);

  // Berechne den Fortschritt in Prozent
  const completionPercentage = useMemo(() => {
    if (!progress) return 0;
    const total = applicableSteps.length;
    const completed = progress.completedSteps.filter(
      stepId => applicableSteps.some(s => s.id === stepId)
    ).length;
    return Math.round((completed / total) * 100);
  }, [progress, applicableSteps]);

  return {
    currentStep: progress?.currentStep || 'welcome',
    progress,
    isLoading,
    error,
    interviewData,
    needsOnboarding,
    isStepCompleted,
    shouldShowStep,
    isCorporation,
    goToNextStep,
    goToPreviousStep,
    skipStep,
    saveStepData,
    completeOnboarding,
    completionPercentage,
    totalSteps: applicableSteps.length,
    currentStepIndex,
  };
};

